import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fetchCorporateDaily, fetchCorporateDailyLocations, fetchFuelTypes } from "../api/client";
import { useTranslation } from "react-i18next";
import { filterDailyRows, hasDailyDisplayScope, reconcileDailyLocationSelection } from "../utils/dailyFilters.js";
import { formatCompactDailyChartLabel } from "../utils/dailyChartLabels.js";
import { buildDailyChartData, getDailyTooltipDetails } from "../utils/dailyChartData.js";

const PROVIDER_LABELS = {
  neste_corp: "Neste",
  st1_for_corporate: "St1",
  abc_corp: "ABC",
};

const siteColors = {
  neste_corp: "#82ca9d",
  st1_for_corporate: "#8884d8",
  abc_corp: "#f29f67",
};

const ECO_FUEL_TYPE_ID = "burning_oil_eco";

const localeCompareFi = (a = "", b = "") =>
  a.localeCompare(b, "fi", { sensitivity: "base" });

const getColor = (key) => {
  if (siteColors[key]) return siteColors[key];
  const hash = [...key].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const hue = hash % 360;
  return `hsl(${hue}, 60%, 55%)`;
};

function formatProductName(item) {
  const baseName =
    item.product_name || item.product_code || item.product_group || "Tuote";
  const resolvedName = baseName;
  if (
    (item.fuelTypeId && item.fuelTypeId === ECO_FUEL_TYPE_ID) ||
    /eco-öljy/i.test(item.fuelTypeLabel || "")
  ) {
    return /hvo/i.test(resolvedName)
      ? resolvedName
      : `${resolvedName} (RE HVO)`;
  }
  return resolvedName;
}

function normalizeRows(items = []) {
  return items
    .map((item, idx) => {
      const provider = item.provider || "unknown";
      const rawLocation =
        item.postal_display_name ||
        item.municipality ||
        item.location ||
        "Unknown";
      const postalCode =
        item.postal_display_code ||
        item.postal_code ||
        "";
      const productName = formatProductName(item);
      const unitPrice =
        typeof item.unit_price === "number"
          ? item.unit_price
          : typeof item.unit_price_excl === "number"
          ? item.unit_price_excl
          : typeof item.unit_price_incl === "number"
          ? item.unit_price_incl
          : null;

      if (unitPrice === null) {
        return null;
      }

      const locationFilterKey =
        rawLocation && postalCode
          ? `${rawLocation} (${postalCode})`
          : rawLocation || postalCode || `LOC-${idx}`;
      const labelBase =
        rawLocation && postalCode
          ? `${rawLocation} (${postalCode})`
          : rawLocation || postalCode || "Tuntematon";

      return {
        id: `${provider}-${rawLocation}-${productName}-${item.product_code || idx}`,
        provider,
        providerLabel: PROVIDER_LABELS[provider] || provider,
        location: rawLocation,
        locationName: rawLocation,
        locationCode: postalCode,
        locationFilterKey,
        productName,
        productGroup: item.product_group || item.product_category || "",
        unit_price: unitPrice,
        currency: item.currency || "EUR",
        label: `${labelBase} – ${productName}`,
        categoryId: item.categoryId || null,
        categoryLabel: item.categoryLabel || null,
        fuelTypeId: item.fuelTypeId || null,
        fuelTypeLabel: item.fuelTypeLabel || null,
      };
    })
    .filter(Boolean);
}

function MultiSelection({ title, options, selected, onToggle, onSelectAll, onClear, allLabel, clearLabel, selectedLabel, noneSelectedLabel }) {
  const selectedLabels = selected.map((id) => options.find((option) => option.id === id)?.label || id);
  return (
    <div>
      <p className="block mb-1 font-medium">{title}</p>
      <div className="flex gap-2 mb-1">
        <button type="button" onClick={onSelectAll} className="bg-blue-500 text-white px-2 py-1 rounded text-sm hover:bg-blue-600">{allLabel}</button>
        <button type="button" onClick={onClear} className="bg-gray-300 text-gray-800 px-2 py-1 rounded text-sm hover:bg-gray-400">{clearLabel}</button>
      </div>
      <div className="max-h-48 overflow-y-auto rounded border bg-white">
        {options.map((option) => <label key={option.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100"><input type="checkbox" checked={selected.includes(option.id)} onChange={() => onToggle(option.id)} /><span>{option.label}</span></label>)}
      </div>
      <p className="mt-2 text-xs font-medium text-gray-600">{selectedLabel}</p>
      <div className="mt-1 min-h-8 rounded border bg-gray-50 p-1">
        {selected.length > 0 ? selectedLabels.map((label, index) => <button key={`${selected[index]}-${label}`} type="button" onClick={() => onToggle(selected[index])} className="m-0.5 rounded bg-blue-100 px-2 py-1 text-left text-xs text-blue-900 hover:bg-blue-200">{label} ×</button>) : <p className="px-1 py-1 text-xs text-gray-500">{noneSelectedLabel}</p>}
      </div>
    </div>
  );
}

function CompactVerticalTick({ x, y, payload }) {
  const lines = String(payload.value).split("\n");
  return (
    <g transform={`translate(${x},${y}) rotate(-45)`}>
      <text textAnchor="end" fill="#374151" fontSize="12">
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x="0" dy={index === 0 ? 0 : 13}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

function DailyPriceTooltip({ active, payload, label, chartData, labelKey, preferPayload }) {
  if (!active || !payload?.length) return null;
  const details = getDailyTooltipDetails({ payload, chartData, label, labelKey, preferPayload });
  if (!details) return null;

  return (
    <div className="rounded border border-gray-300 bg-white p-3 text-sm shadow">
      <p className="font-semibold">{details.locationName} ({details.locationCode || "—"})</p>
      <p className="mb-2">{details.productName}</p>
      {payload.filter((item) => typeof item.value === "number").map((item) => (
        <p key={item.dataKey} className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
          {item.name}: {item.value.toFixed(4)} €/L
        </p>
      ))}
    </div>
  );
}

export default function PriceChart() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const [locations, setLocations] = useState([]);
  const [providers] = useState(Object.keys(PROVIDER_LABELS));
  const [products, setProducts] = useState([]);
  const [fuelCategories, setFuelCategories] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [locationSearchInput, setLocationSearchInput] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [locationBrowseMode, setLocationBrowseMode] = useState("scheduled");
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedFuelTypes, setSelectedFuelTypes] = useState([]);

  const [yScaleMode, setYScaleMode] = useState("auto");
  const [customYMin, setCustomYMin] = useState("");
  const [customYMax, setCustomYMax] = useState("");
  const [chartOrientation, setChartOrientation] = useState("vertical");

  const [sortConfig, setSortConfig] = useState({
    key: "location",
    direction: "asc",
  });

  useEffect(() => {
    fetchFuelTypes().then((categories) => {
      setFuelCategories((categories || []).map((group) => ({ id: group.id, label: group.label })));
      setFuelTypes((categories || []).flatMap((group) => group.variants?.map((variant) => ({ id: variant.id, label: `${group.label} – ${variant.label}`, categoryId: group.id })) || []));
    }).catch(() => { setFuelCategories([]); setFuelTypes([]); });
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setLocationQuery(locationSearchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [locationSearchInput]);

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setIsLoadingLocations(true);
    fetchCorporateDailyLocations({ query: locationQuery || undefined, scope: locationBrowseMode === "scheduled" ? "scheduled" : undefined, signal: controller.signal })
      .then((response) => {
        if (!current) return;
        const options = response.recommended && !response.items.some((item) => item.id === response.recommended.id) ? [response.recommended, ...response.items] : response.items;
        setLocations(options);
        setSelectedLocations((previous) => reconcileDailyLocationSelection({
          previous,
          optionIds: options.map((option) => option.id),
          recommendedId: response.recommended?.id,
          isSearching: Boolean(locationQuery),
        }));
      })
      .catch((err) => { if (err.code !== "ERR_CANCELED" && current) { setError(err.response?.data?.message || t("chart.errors.locations")); setLocations([]); } })
      .finally(() => { if (current) setIsLoadingLocations(false); });
    return () => { current = false; controller.abort(); };
  }, [locationQuery, locationBrowseMode, t]);

  useEffect(() => {
    if (selectedLocations.length === 0) { setRows([]); setProducts([]); return undefined; }
    const controller = new AbortController();
    let current = true;
    setIsLoadingData(true);
    setRows([]);
    setProducts([]);
    fetchCorporateDaily({ locations: selectedLocations, providers: selectedProviders, signal: controller.signal })
      .then((response) => {
        if (!current) return;
        const normalized = filterDailyRows(normalizeRows(response.items), {
          locations: selectedLocations,
          providers: selectedProviders,
        });
        setRows(normalized);
        setProducts([...new Set(normalized.map((row) => row.productName))]);
        setError(null);
      })
      .catch((err) => { if (err.code !== "ERR_CANCELED" && current) { setError(err.response?.data?.message || t("chart.error")); setRows([]); } })
      .finally(() => { if (current) setIsLoadingData(false); });
    return () => { current = false; controller.abort(); };
  }, [selectedLocations, selectedProviders, t]);

  const filteredRows = useMemo(() => filterDailyRows(rows, {
    locations: selectedLocations, providers: selectedProviders, products: selectedProducts,
    categories: selectedCategories, fuelTypes: selectedFuelTypes,
  }), [rows, selectedLocations, selectedProviders, selectedProducts, selectedCategories, selectedFuelTypes]);

  const canDisplayResults = hasDailyDisplayScope({
    locations: selectedLocations, providers: selectedProviders, products: selectedProducts,
    categories: selectedCategories, fuelTypes: selectedFuelTypes,
  });
  const selectionKey = [selectedLocations, selectedProviders, selectedProducts, selectedCategories, selectedFuelTypes]
    .map((values) => [...values].sort().join(",")).join("|");

  const availableFuelTypes = useMemo(() => {
    if (selectedCategories.length === 0) return fuelTypes;
    const allowed = new Set(selectedCategories);
    return fuelTypes.filter((type) => allowed.has(type.categoryId));
  }, [fuelTypes, selectedCategories]);

  useEffect(() => {
    setSelectedFuelTypes((prev) =>
      prev.filter((id) => availableFuelTypes.some((type) => type.id === id))
    );
  }, [availableFuelTypes]);

  const availableProducts = useMemo(() => {
    const allowedCategories =
      selectedCategories.length > 0 ? new Set(selectedCategories) : null;
    const allowedFuelTypes =
      selectedFuelTypes.length > 0 ? new Set(selectedFuelTypes) : null;
    const names = new Set();
    rows.forEach((row) => {
      if (allowedCategories && (!row.categoryId || !allowedCategories.has(row.categoryId))) {
        return;
      }
      if (allowedFuelTypes && (!row.fuelTypeId || !allowedFuelTypes.has(row.fuelTypeId))) {
        return;
      }
      names.add(row.productName);
    });
    if (names.size === 0) {
      return [...products].sort((a, b) => localeCompareFi(a, b));
    }
    return Array.from(names).sort((a, b) => localeCompareFi(a, b));
  }, [rows, products, selectedCategories, selectedFuelTypes]);

  useEffect(() => {
    setSelectedProducts((prev) =>
      prev.filter((name) => availableProducts.includes(name))
    );
  }, [availableProducts]);

  const chartData = useMemo(
    () => buildDailyChartData(filteredRows, formatCompactDailyChartLabel),
    [filteredRows]
  );

  const priceValues = filteredRows
    .map((row) => row.unit_price)
    .filter((value) => typeof value === "number" && !Number.isNaN(value));

  const suggestedMin = priceValues.length ? Math.min(...priceValues) : null;
  const suggestedMax = priceValues.length ? Math.max(...priceValues) : null;

  const providersToRender =
    selectedProviders.length > 0 ? selectedProviders : providers;

  const chartHeight =
    chartOrientation === "horizontal"
      ? Math.max(400, chartData.length * 44 + 80)
      : 460;

  const yDomain = useMemo(() => {
    if (yScaleMode !== "custom") return ["auto", "auto"];
    const minValid = customYMin !== "" && !Number.isNaN(Number(customYMin));
    const maxValid = customYMax !== "" && !Number.isNaN(Number(customYMax));

    if (minValid && maxValid) {
      const minVal = Number(customYMin);
      const maxVal = Number(customYMax);
      return [Math.min(minVal, maxVal), Math.max(minVal, maxVal)];
    }
    if (minValid) return [Number(customYMin), "auto"];
    if (maxValid) return ["auto", Number(customYMax)];
    return ["auto", "auto"];
  }, [yScaleMode, customYMin, customYMax]);

  const applySuggestedRange = () => {
    if (suggestedMin === null || suggestedMax === null) return;
    const padding = 0.02;
    const minValue = Math.max(suggestedMin - padding, 0);
    setCustomYMin(minValue.toFixed(2));
    setCustomYMax((suggestedMax + padding).toFixed(2));
    setYScaleMode("custom");
  };

  const handleSelectAll = (list, setter) => setter(list);
  const handleClearAll = (setter) => setter([]);
  const toggleSelection = (value, setter) => setter((previous) => previous.includes(value) ? previous.filter((item) => item !== value) : [...previous, value]);
  const showLocationList = (mode) => {
    setLocationSearchInput("");
    setLocationQuery("");
    setLocationBrowseMode(mode);
  };

  const sortedTableRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];
      if (valA === valB) return 0;
      if (sortConfig.direction === "asc") {
        return valA > valB ? 1 : -1;
      }
      return valA < valB ? 1 : -1;
    });
    return copy;
  }, [
    filteredRows,
    sortConfig,
  ]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-xl font-bold mb-4">
        {t("chart.title")}
      </h1>
      {error && <p className="text-red-500">{error}</p>}

      {/* Filters */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div>
          <label className="block mb-1 font-medium">{t("chart.location")}</label>
          <div className="flex gap-2 mb-1">
            <button type="button" onClick={() => showLocationList("scheduled")} className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 text-sm">{t("chart.locations.scheduled")}</button>
            <button type="button" onClick={() => showLocationList("daily")} className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 text-sm">{t("chart.locations.all")}</button>
            <button type="button" onClick={() => handleClearAll(setSelectedLocations)} className="bg-gray-300 text-gray-800 px-2 py-1 rounded hover:bg-gray-400 text-sm">{t("common.clear")}</button>
          </div>
          <input type="search" value={locationSearchInput} onChange={(event) => setLocationSearchInput(event.target.value)} placeholder={t("chart.locations.searchPlaceholder")} className="border p-2 rounded w-full text-sm" />
          <div className="mt-2 max-h-48 overflow-y-auto rounded border bg-white">
            {isLoadingLocations && <p className="p-2 text-sm text-gray-500">{t("common.loading")}</p>}
            {!isLoadingLocations && locations.map((loc) => <label key={loc.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100"><input type="checkbox" checked={selectedLocations.includes(loc.id)} onChange={() => toggleSelection(loc.id, setSelectedLocations)} /><span>{loc.label}</span></label>)}
          </div>
          <p className="mt-2 text-xs font-medium text-gray-600">{t("chart.locations.selected")}</p>
          <div className="mt-1 min-h-8 rounded border bg-gray-50 p-1">{selectedLocations.length > 0 ? selectedLocations.map((id) => <button key={id} type="button" onClick={() => toggleSelection(id, setSelectedLocations)} className="m-0.5 rounded bg-blue-100 px-2 py-1 text-left text-xs text-blue-900 hover:bg-blue-200">{locations.find((location) => location.id === id)?.label || id} ×</button>) : <p className="px-1 py-1 text-xs text-gray-500">{t("chart.locations.noneSelected")}</p>}</div>
        </div>
        <MultiSelection title={t("chart.provider")} options={providers.map((id) => ({ id, label: PROVIDER_LABELS[id] || id }))} selected={selectedProviders} onToggle={(id) => toggleSelection(id, setSelectedProviders)} onSelectAll={() => handleSelectAll(providers, setSelectedProviders)} onClear={() => handleClearAll(setSelectedProviders)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("chart.selected")} noneSelectedLabel={t("chart.noneSelected")} />
        <MultiSelection title={t("chart.product")} options={availableProducts.map((id) => ({ id, label: id }))} selected={selectedProducts} onToggle={(id) => toggleSelection(id, setSelectedProducts)} onSelectAll={() => handleSelectAll(availableProducts, setSelectedProducts)} onClear={() => handleClearAll(setSelectedProducts)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("chart.selected")} noneSelectedLabel={t("chart.noneSelected")} />
        <MultiSelection title={t("chart.fuelCategory")} options={fuelCategories} selected={selectedCategories} onToggle={(id) => toggleSelection(id, setSelectedCategories)} onSelectAll={() => handleSelectAll(fuelCategories.map((item) => item.id), setSelectedCategories)} onClear={() => handleClearAll(setSelectedCategories)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("chart.selected")} noneSelectedLabel={t("chart.noneSelected")} />
        <MultiSelection title={t("chart.fuelType")} options={availableFuelTypes} selected={selectedFuelTypes} onToggle={(id) => toggleSelection(id, setSelectedFuelTypes)} onSelectAll={() => handleSelectAll(availableFuelTypes.map((item) => item.id), setSelectedFuelTypes)} onClear={() => handleClearAll(setSelectedFuelTypes)} allLabel={t("common.all")} clearLabel={t("common.clear")} selectedLabel={t("chart.selected")} noneSelectedLabel={t("chart.noneSelected")} />
      </div>

      {/* Chart */}
      <div className="border rounded-lg p-4 bg-white shadow-sm">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">{t("chart.comparison")}</h2>
          <div className="flex flex-wrap gap-3 text-sm">
            {providersToRender.map((provider) => (
              <span key={provider} className="inline-flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: getColor(provider) }} />
                {PROVIDER_LABELS[provider] || provider}
              </span>
            ))}
          </div>
        </div>

        <fieldset className="mb-4 flex flex-wrap items-center gap-3 text-sm">
          <legend className="mr-2 inline font-medium">{t("chart.orientation")}</legend>
          <label className="flex items-center gap-2">
            <input type="radio" name="chart-orientation" value="vertical" checked={chartOrientation === "vertical"} onChange={() => setChartOrientation("vertical")} />
            <span>{t("chart.vertical")}</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="chart-orientation" value="horizontal" checked={chartOrientation === "horizontal"} onChange={() => setChartOrientation("horizontal")} />
            <span>{t("chart.horizontal")}</span>
          </label>
        </fieldset>

        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="font-medium block mb-1">{t("chart.yScale")}</label>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="y-scale"
                  value="auto"
                  checked={yScaleMode === "auto"}
                  onChange={() => setYScaleMode("auto")}
                />
                <span>
                  {t("chart.automatic")}{" "}
                  {suggestedMin !== null && suggestedMax !== null
                    ? `(≈ ${suggestedMin.toFixed(3)} – ${suggestedMax.toFixed(
                        3
                      )} €/L)`
                    : ""}
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="y-scale"
                  value="custom"
                  checked={yScaleMode === "custom"}
                  onChange={() => setYScaleMode("custom")}
                />
                <span>{t("chart.customRange")}</span>
              </label>
            </div>
          </div>

          <div>
            <label className="font-medium block mb-1">{t("chart.minPrice")}</label>
            <input
              type="number"
              step="0.01"
              placeholder={suggestedMin !== null ? suggestedMin.toFixed(2) : t("chart.automatic")}
              value={customYMin}
              onChange={(e) => setCustomYMin(e.target.value)}
              disabled={yScaleMode !== "custom"}
              className="border rounded px-3 py-2 w-full disabled:bg-gray-100"
            />
          </div>

  <div>
            <label className="font-medium block mb-1">{t("chart.maxPrice")}</label>
            <input
              type="number"
              step="0.01"
              placeholder={suggestedMax !== null ? suggestedMax.toFixed(2) : t("chart.automatic")}
              value={customYMax}
              onChange={(e) => setCustomYMax(e.target.value)}
              disabled={yScaleMode !== "custom"}
              className="border rounded px-3 py-2 w-full disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
          <button
            onClick={applySuggestedRange}
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={suggestedMin === null || suggestedMax === null}
          >
            {t("chart.fitToData")}
          </button>
          {yScaleMode === "custom" && (
            <span className="text-gray-500">
              {t("chart.customRangeHint")}
            </span>
          )}
        </div>

        {isLoadingData && <p className="text-sm text-gray-500 mb-2">{t("common.loading")}</p>}
        {canDisplayResults ? <div className="w-full overflow-x-auto" style={{ maxHeight: "70vh" }}>
          <div style={{ minWidth: "900px", height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout={chartOrientation === "horizontal" ? "vertical" : undefined}
                margin={chartOrientation === "horizontal"
                  ? { top: 20, right: 30, left: 250, bottom: 30 }
                  : { top: 20, right: 30, left: 20, bottom: 95 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {chartOrientation === "horizontal" ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 12 }} domain={yDomain} label={{ value: "€/L", position: "insideBottom", offset: -10 }} />
                    <YAxis type="category" dataKey="label" width={230} tick={{ fontSize: 12 }} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="compactLabel" interval={0} height={85} tick={<CompactVerticalTick />} />
                    <YAxis domain={yDomain} tick={{ fontSize: 12 }} label={{ value: "€/L", angle: -90, position: "insideLeft" }} />
                  </>
                )}
                <Tooltip
                  shared={chartOrientation === "vertical" ? false : undefined}
                  isAnimationActive={false}
                  offset={6}
                  content={(tooltipProps) => (
                  <DailyPriceTooltip
                    {...tooltipProps}
                    chartData={chartData}
                    labelKey={chartOrientation === "vertical" ? "compactLabel" : "label"}
                    preferPayload={chartOrientation === "vertical"}
                  />
                  )}
                />
                {providersToRender.map((provider) => (
                  <Bar
                    key={provider}
                    dataKey={provider}
                    name={PROVIDER_LABELS[provider] || provider}
                    fill={getColor(provider)}
                    barSize={40}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div> : <p className="py-12 text-center text-gray-500">{t("chart.selectionRequired")}</p>}
      </div>

      {/* Sortable Table */}
      {canDisplayResults && <div className="overflow-x-auto mt-8 h-96 overflow-y-auto">
        <h2 className="text-lg font-semibold mb-2">{t("chart.rowDetails")}</h2>
        <table className="min-w-full border border-gray-300 text-sm">
          <thead className="bg-gray-100">
            <tr>
              {[
                { key: "location", label: t("chart.table.location") },
                { key: "providerLabel", label: t("chart.table.provider") },
                { key: "productName", label: t("chart.table.product") },
                { key: "categoryLabel", label: t("chart.table.category") },
                { key: "fuelTypeLabel", label: t("chart.table.fuelType") },
                { key: "unit_price", label: t("chart.table.unitPrice") },
              ].map((col) => (
                <th
                  key={col.key}
                  className="border px-3 py-2 text-left cursor-pointer select-none hover:bg-gray-200"
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}{" "}
                  <span className="text-gray-500">{getSortIndicator(col.key)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody key={selectionKey}>
            {sortedTableRows.length > 0 ? (
              sortedTableRows.map((row) => (
                <tr
                  key={row.id}
                  className="odd:bg-white even:bg-gray-50 hover:bg-blue-50"
                >
                  <td className="border px-3 py-2">
                    <div className="font-medium">
                      {row.locationName || row.location}
                    </div>
                    {(row.locationCode || row.postal_display_code) && (
                      <div className="text-xs text-gray-500">
                        {row.locationCode || row.postal_display_code}
                      </div>
                    )}
                  </td>
                  <td className="border px-3 py-2 font-medium">
                    <span
                      className="inline-block w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: getColor(row.provider) }}
                    ></span>
                    {row.providerLabel}
                  </td>
                  <td className="border px-3 py-2">{row.productName}</td>
                  <td className="border px-3 py-2">
                    {row.categoryLabel || "—"}
                  </td>
                  <td className="border px-3 py-2">
                    {row.fuelTypeLabel || "—"}
                  </td>
                  <td className="border px-3 py-2 text-right">
                    {row.unit_price?.toFixed(4)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-4 text-gray-500">
                  {t("chart.noRows")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>}
    </div>
  );
}
